import { FormEvent, useState } from 'react'
import { showcaseCategories } from '../data/libraryShowcase'
import {
  ArrowRight,
  Check,
  ChevronDown,
  Download,
  Headphones,
  Languages,
  MessageCircle,
  MonitorSmartphone,
  Moon,
  Plane,
  Play,
  Sparkles,
  Star,
  Target,
  WifiOff,
} from 'lucide-react'

// Reuse the exact English WebP cover bundle shipped by the offline app.
import thinkingCover from '../../../app/assets/book-covers/en/015.webp'
import habitCover from '../../../app/assets/book-covers/en/012.webp'
import duneCover from '../../../app/assets/book-covers/en/305.webp'
import gritCover from '../../../app/assets/book-covers/en/182.webp'
import powerNowCover from '../../../app/assets/book-covers/en/117.webp'

const biblio = '/biblio-confident.png'
const biblioHappy = '/biblio-happy.png'

// Official App Store artwork, also used as the navbar/footer brand mark.
const brandIcon = '/app-store-icon.jpg'

// Live Apple App Store listing (approved). The iOS badge links here directly.
const APP_STORE_URL = 'https://apps.apple.com/us/app/10-000-offline-books-in-10-min/id6785132606'

interface LandingPageProps {
  mode: 'waitlist' | 'launch'
  appName?: string
  tagline?: string
  description?: string
  screenshotUrl?: string
  iosUrl?: string
  androidUrl?: string
  externalTestingEndpoint?: string
  webUrl?: string
}

const books = [
  { cover: thinkingCover, title: 'Thinking, Fast and Slow', category: 'Psychology', color: '#e8a34a' },
  { cover: habitCover, title: 'The Power of Habit', category: 'Productivity', color: '#e9674d' },
  { cover: duneCover, title: 'Dune', category: 'Sci-Fi', color: '#466558' },
  { cover: gritCover, title: 'Grit', category: 'Motivation', color: '#6259a7' },
  { cover: powerNowCover, title: 'The Power of Now', category: 'Mindfulness', color: '#318f86' },
]

const benefits = [
  {
    icon: WifiOff,
    number: '01',
    title: 'A library that never loses signal.',
    description: 'Every summary lives on your device. Read on the subway, listen on a flight, keep learning far beyond Wi-Fi.',
    accent: 'mint',
  },
  {
    icon: Headphones,
    number: '02',
    title: 'Read it. Or let it read to you.',
    description: 'Natural read-aloud, adjustable speed, and live sentence highlighting turn any quiet minute into learning time.',
    accent: 'coral',
  },
  {
    icon: Target,
    number: '03',
    title: 'A habit designed to stick.',
    description: 'Daily goals, streaks, milestones, and a tiny bookish companion make showing up feel genuinely rewarding.',
    accent: 'violet',
  },
]

const faqs = [
  ['Does everything really work offline?', 'Reading, listening, saved progress, and streaks all work without a connection. Only conversations with Biblio need internet.'],
  ['What languages are available?', 'English, French, and Spanish are first-class throughout the app — including every available summary.'],
  ['Can the app read every book aloud?', 'Yes. It uses your device’s natural voice, with speed controls and sentence highlighting so you can listen or follow along.'],
  ['Can I try it before subscribing?', 'Yes. Explore the guided in-app preview, and the annual membership includes a 3-day free trial.'],
]

function StoreButtons({
  webUrl,
  iosUrl,
  compact = false,
  onRequestExternalTesting,
}: {
  webUrl: string
  iosUrl: string
  compact?: boolean
  onRequestExternalTesting: (platform: 'ios' | 'android') => void
}) {
  return (
    <div className={`store-buttons ${compact ? 'store-buttons--compact' : ''}`}>
      <a className="store-button" href={iosUrl} target="_blank" rel="noreferrer" aria-label="Download on the App Store">
        <img src="/app-store-badge.svg" alt="Download on the App Store" />
      </a>
      <button className="store-button" type="button" onClick={() => onRequestExternalTesting('android')} aria-label="Join Google Play external testing">
        <img src="/google-play-badge.svg" alt="Get it on Google Play" />
      </button>
      <a className="web-app-button" href={webUrl} target="_blank" rel="noreferrer">
        <MonitorSmartphone size={20} />
        <span><small>AVAILABLE NOW</small><strong>Open web app</strong></span>
        <ArrowRight size={16} />
      </a>
    </div>
  )
}

export function LandingPage({
  mode,
  appName = '10K Books',
  iosUrl,
  externalTestingEndpoint,
  webUrl = 'https://app.10000-offline-books-in-10-min.altuzar.dev/',
}: LandingPageProps) {
  // iOS is live on the App Store: always resolve to a direct store link,
  // even when VITE_IOS_APP_URL is unset or empty.
  const resolvedIosUrl = iosUrl || APP_STORE_URL
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [testingPlatform, setTestingPlatform] = useState<'ios' | 'android' | null>(null)
  const [testingEmail, setTestingEmail] = useState('')
  const [testingStatus, setTestingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [testingError, setTestingError] = useState('')
  const [activeCategoryKey, setActiveCategoryKey] = useState(showcaseCategories[0].key)
  const [allCategoriesVisible, setAllCategoriesVisible] = useState(false)
  const activeCategory = showcaseCategories.find(category => category.key === activeCategoryKey) ?? showcaseCategories[0]
  const visibleCategories = allCategoriesVisible ? showcaseCategories : showcaseCategories.slice(0, 5)
  const isWaitlist = mode === 'waitlist'

  const openExternalTesting = (platform: 'ios' | 'android') => {
    setTestingPlatform(platform)
    setTestingStatus('idle')
    setTestingError('')
  }

  const closeExternalTesting = () => {
    if (testingStatus !== 'loading') setTestingPlatform(null)
  }

  const submitExternalTesting = async (event: FormEvent) => {
    event.preventDefault()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testingEmail)) {
      setTestingStatus('error')
      setTestingError('Enter a valid email address.')
      return
    }
    if (!testingPlatform || !externalTestingEndpoint) {
      setTestingStatus('error')
      setTestingError('External testing is not available yet. Please try again shortly.')
      return
    }

    setTestingStatus('loading')
    setTestingError('')
    try {
      const response = await fetch(externalTestingEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testingEmail, platform: testingPlatform }),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.error || 'Unable to save your request.')
      setTestingStatus('success')
      setTestingEmail('')
    } catch (error) {
      setTestingStatus('error')
      setTestingError(error instanceof Error ? error.message : 'Unable to save your request.')
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error')
      setErrorMessage('Enter a valid email to join the list.')
      return
    }

    setStatus('loading')
    setErrorMessage('')
    try {
      const endpoint = import.meta.env.VITE_WAITLIST_API_ENDPOINT
      if (endpoint) {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
        if (!response.ok) throw new Error('Submission failed')
      } else {
        await new Promise(resolve => setTimeout(resolve, 650))
      }
      setStatus('success')
      setEmail('')
    } catch {
      setStatus('error')
      setErrorMessage('Something went wrong. Please try again.')
    }
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label={`${appName} home`}>
          <img className="brand-mark" src={brandIcon} alt="" width={37} height={37} />
          <span className="brand-name"><strong>10K</strong> Books</span>
        </a>
        <nav className="desktop-nav" aria-label="Main navigation">
          <a href="#experience">Experience</a>
          <a href="#library">Library</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>
        <a className="nav-cta" href={isWaitlist ? '#waitlist' : webUrl} target={isWaitlist ? undefined : '_blank'} rel={isWaitlist ? undefined : 'noreferrer'}>
          {isWaitlist ? 'Join waitlist' : 'Open web app'} <ArrowRight size={15} />
        </a>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero-glow hero-glow--one" />
          <div className="hero-glow hero-glow--two" />
          <div className="hero-copy">
            <div className="eyebrow"><Sparkles size={15} fill="currentColor" /> Big ideas, beautifully distilled</div>
            <h1>Read <em>10,000 books.</em><br />10 minutes at a time.</h1>
            <p className="hero-description">The world’s most powerful ideas — made clear, memorable, and available anywhere. Read or listen offline in English, French, or Spanish.</p>

            {isWaitlist ? (
              <div id="waitlist" className="waitlist-wrap">
                {status === 'success' ? (
                  <div className="success-message"><span><Check size={18} /></span><div><strong>You’re in.</strong><small>We’ll let you know the moment Biblio is ready.</small></div></div>
                ) : (
                  <form className="waitlist-form" onSubmit={handleSubmit}>
                    <label className="sr-only" htmlFor="email">Email address</label>
                    <input id="email" type="email" placeholder="Your email address" value={email} onChange={event => setEmail(event.target.value)} disabled={status === 'loading'} />
                    <button type="submit" disabled={status === 'loading'}>{status === 'loading' ? 'Joining…' : 'Get early access'} <ArrowRight size={17} /></button>
                  </form>
                )}
                {status === 'error' && <p className="form-error">{errorMessage}</p>}
              </div>
            ) : (
              <StoreButtons webUrl={webUrl} iosUrl={resolvedIosUrl} onRequestExternalTesting={openExternalTesting} />
            )}

            <div className="hero-trust">
              <div className="avatar-stack" aria-hidden="true"><img src="/avatar-1.jpeg" alt="" width={29} height={29} /><img src="/avatar-2.jpeg" alt="" width={29} height={29} /><img src="/avatar-3.jpeg" alt="" width={29} height={29} /><img src="/avatar-4.jpeg" alt="" width={29} height={29} /></div>
              <div><div className="stars"><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /></div><p>Loved by curious minds with no time to waste</p></div>
            </div>
          </div>

          <div className="hero-stage" aria-label="Preview of the reading experience">
            <div className="orbit orbit--one" />
            <div className="orbit orbit--two" />
            <div className="floating-note floating-note--offline"><Plane size={16} /><span><strong>Plane mode?</strong>No problem.</span></div>
            <div className="floating-note floating-note--streak"><span className="flame">7</span><span><strong>Day streak</strong>Personal best</span></div>

            <div className="phone">
              <div className="phone-top"><span>9:41</span><i /><span>•••</span></div>
              <div className="phone-screen">
                <div className="app-greeting"><div><small>Good morning</small><strong>What will you learn today?</strong></div><img src={biblio} alt="Biblio, your reading companion" /></div>
                <div className="goal-card">
                  <div className="goal-ring"><span>8</span><small>min</small></div>
                  <div><small>DAILY GOAL</small><strong>You’re nearly there</strong><p>Two more minutes to keep your streak.</p></div>
                </div>
                <div className="continue-title"><strong>Continue learning</strong><span>See all</span></div>
                <div className="current-book">
                  <img src={thinkingCover} alt="Thinking, Fast and Slow cover" />
                  <div><small>PSYCHOLOGY · 12 MIN</small><strong>Thinking, Fast and Slow</strong><p>Daniel Kahneman</p><div className="progress"><i /></div><span>62% complete</span></div>
                  <button aria-label="Resume reading"><Play size={15} fill="currentColor" /></button>
                </div>
                <div className="app-section-label"><strong>Made for your morning</strong><span>View all</span></div>
                <div className="mini-books">{books.slice(1, 4).map(book => <img key={book.title} src={book.cover} alt={`${book.title} cover`} />)}</div>
              </div>
            </div>

            <div className="biblio-float"><img src={biblioHappy} alt="Biblio celebrating" /><div><small>BIBLIO SAYS</small><strong>That’s 7 books this week!</strong></div></div>
          </div>
        </section>

        <section className="proof-bar" aria-label="Product highlights">
          <div><strong>1,000+</strong><span>expert summaries</span></div><i />
          <div><strong>22</strong><span>curated categories</span></div><i />
          <div><strong>3</strong><span>complete languages</span></div><i />
          <div><WifiOff size={22} /><span>built for offline</span></div>
        </section>

        <section className="library-section" id="library">
          <div className="section-heading">
            <div><span className="kicker">A library for the life you’re building</span><h2>Ideas worth carrying with you.</h2></div>
            <p>From psychology to finance, timeless classics to modern breakthroughs — each summary is curated to give you the ideas that matter and leave out what doesn’t.</p>
          </div>
          <div className="book-shelf" key={activeCategory.key}>
            {activeCategory.books.map((book, index) => (
              <article className={`book-card book-card--${index + 1} book-card--enter`} style={{ '--book-color': book.color, animationDelay: `${index * 70}ms` } as React.CSSProperties} key={book.id}>
                <div className="cover-wrap"><img src={book.cover} alt={`${book.title} book cover`} loading="lazy" /></div>
                <span>{activeCategory.label}</span><strong>{book.title}</strong><small>{book.readMinutes} min</small>
              </article>
            ))}
          </div>
          <div className="category-row" role="group" aria-label="Browse books by category">
            {visibleCategories.map(category => (
              <button
                key={category.key}
                type="button"
                className={category.key === activeCategory.key ? 'category-pill category-pill--active' : 'category-pill'}
                onClick={() => setActiveCategoryKey(category.key)}
                aria-pressed={category.key === activeCategory.key}
              >
                {category.label}
              </button>
            ))}
            <button
              type="button"
              className="category-pill category-pill--more"
              onClick={() => setAllCategoriesVisible(visible => !visible)}
              aria-expanded={allCategoriesVisible}
            >
              {allCategoriesVisible ? 'Show less' : `+ ${showcaseCategories.length - visibleCategories.length} more`}
            </button>
          </div>
        </section>

        <section className="experience-section" id="experience">
          <div className="experience-intro">
            <span className="kicker kicker--light">Designed around real life</span>
            <h2>Your curiosity doesn’t need a connection.</h2>
            <p>Download once. Keep every idea, every highlight, and every bit of progress right where it belongs — with you.</p>
            <div className="offline-pills"><span><Plane size={15} /> Flights</span><span><Moon size={15} /> Bedtime</span><span><Download size={15} /> Commutes</span></div>
          </div>
          <div className="reader-preview">
            <div className="reader-toolbar"><span>‹</span><strong>Thinking, Fast and Slow</strong><span>•••</span></div>
            <div className="reader-body"><small>KEY IDEA · 04</small><h3>Your mind has two ways of thinking.</h3><p>System 1 is fast, intuitive, and emotional. System 2 is slower, more deliberate, and more logical.</p><p className="highlight">Most of the time, the first system is quietly steering the decisions you believe are rational.</p></div>
            <div className="audio-player"><button aria-label="Play summary"><Play size={17} fill="currentColor" /></button><div><span><i /></span><small>04:12</small></div><strong>1×</strong></div>
          </div>
        </section>

        <section className="benefits-section">
          <div className="center-heading"><span className="kicker">Built to fit your day</span><h2>Less scrolling.<br /><em>More becoming.</em></h2></div>
          <div className="benefit-grid">
            {benefits.map(item => (
              <article className={`benefit-card benefit-card--${item.accent}`} key={item.number}>
                <div className="benefit-top"><span>{item.number}</span><div><item.icon size={24} /></div></div>
                <h3>{item.title}</h3><p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="biblio-section">
          <div className="biblio-visual">
            <div className="chat-bubble chat-bubble--question">Why do habits feel so hard to change?</div>
            <img src={biblio} alt="Biblio, the AI reading companion" />
            <div className="chat-bubble chat-bubble--answer"><span><Sparkles size={13} /> Biblio</span>Because habits aren’t just choices — they’re loops. Change the cue or reward, and the routine becomes much easier to reshape.</div>
          </div>
          <div className="biblio-copy"><span className="kicker">Meet Biblio</span><h2>The study buddy who actually read the book.</h2><p>Ask a question, unpack a difficult idea, or connect what you’re reading to something you already know. Biblio keeps every answer grounded in the book.</p><ul><li><MessageCircle size={18} /> Concise, contextual answers</li><li><Languages size={18} /> Speaks English, French, and Spanish</li><li><Sparkles size={18} /> Helps ideas stick, without the jargon</li></ul></div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="center-heading"><span className="kicker">One membership. Every device.</span><h2>Choose how you want to grow.</h2><p>Start on the web today. Your membership follows your account across web, iPhone, and Android.</p></div>
          <div className="pricing-grid">
            <article className="price-card"><span>Weekly</span><div><strong>$4.99</strong><small>/ week</small></div><p>Maximum flexibility. Cancel whenever you like.</p><a href={webUrl} target="_blank" rel="noreferrer">Start on web <ArrowRight size={15} /></a></article>
            <article className="price-card"><span>Monthly</span><div><strong>$9.99</strong><small>/ month</small></div><p>A simple monthly rhythm for curious minds.</p><a href={webUrl} target="_blank" rel="noreferrer">Start on web <ArrowRight size={15} /></a></article>
            <article className="price-card price-card--featured"><em>BEST VALUE</em><span>Yearly</span><div><strong>$59.99</strong><small>/ year</small></div><p><b>3 days free.</b> Then less than the price of one book per month.</p><a href={webUrl} target="_blank" rel="noreferrer">Try on web <ArrowRight size={15} /></a></article>
            <article className="price-card"><span>Lifetime</span><div><strong>$129.99</strong><small>once</small></div><p>Pay once. Keep reading and listening forever.</p><a href={webUrl} target="_blank" rel="noreferrer">Get it on web <ArrowRight size={15} /></a></article>
          </div>
          <p className="pricing-note"><Check size={14} /> Cancel anytime <Check size={14} /> Progress syncs automatically <Check size={14} /> One account, all devices</p>
        </section>

        <section className="faq-section" id="faq">
          <div><span className="kicker">Good questions</span><h2>Everything you need to know.</h2><p>Still curious? Biblio would approve.</p></div>
          <div className="faq-list">
            {faqs.map(([question, answer]) => <details key={question}><summary>{question}<ChevronDown size={19} /></summary><p>{answer}</p></details>)}
          </div>
        </section>

        <section className="closing-section">
          <div className="closing-orbit" /><img src={biblioHappy} alt="Biblio cheering you on" />
          <div><span className="kicker kicker--light">A small ritual. A remarkable difference.</span><h2>Your next 10 minutes could change how you think.</h2><p>One idea today. Another tomorrow. Before long, you’ve built a mind full of better questions.</p>{isWaitlist ? <a className="closing-cta" href="#waitlist">Join the waitlist <ArrowRight size={17} /></a> : <StoreButtons webUrl={webUrl} iosUrl={resolvedIosUrl} compact onRequestExternalTesting={openExternalTesting} />}</div>
        </section>
      </main>

      {testingPlatform && (
        <div className="testing-modal-backdrop" role="presentation" onMouseDown={closeExternalTesting}>
          <section className="testing-modal" role="dialog" aria-modal="true" aria-labelledby="testing-modal-title" onMouseDown={event => event.stopPropagation()}>
            <button className="testing-modal-close" type="button" onClick={closeExternalTesting} aria-label="Close external testing signup">×</button>
            {testingStatus === 'success' ? (
              <div className="testing-success">
                <span><Check size={24} /></span>
                <h2 id="testing-modal-title">You’re on the list.</h2>
                <p>We’ll email your {testingPlatform === 'ios' ? 'Apple TestFlight' : 'Google Play'} invitation as soon as your spot is ready. Your Lifetime Pro access is reserved.</p>
                <button type="button" onClick={closeExternalTesting}>Done</button>
              </div>
            ) : (
              <>
                <span className="testing-modal-kicker">{testingPlatform === 'ios' ? 'APPLE TESTFLIGHT' : 'GOOGLE PLAY'} · EXTERNAL TESTING</span>
                <h2 id="testing-modal-title">Help shape 10K Books. Keep Pro forever.</h2>
                <p>Join our {testingPlatform === 'ios' ? 'Apple TestFlight' : 'Google Play'} external test. In return, you’ll receive <strong>Lifetime Pro free, forever.</strong></p>
                <form onSubmit={submitExternalTesting}>
                  <label htmlFor="external-testing-email">Your email for the invitation</label>
                  <input id="external-testing-email" type="email" autoComplete="email" placeholder="you@example.com" value={testingEmail} onChange={event => setTestingEmail(event.target.value)} disabled={testingStatus === 'loading'} />
                  {testingStatus === 'error' && <p className="form-error">{testingError}</p>}
                  <button type="submit" disabled={testingStatus === 'loading'}>{testingStatus === 'loading' ? 'Saving your spot…' : 'Request my tester invite'} <ArrowRight size={17} /></button>
                </form>
                <small className="testing-note">No payment required. We’ll only use your email to send your testing invite.</small>
              </>
            )}
          </section>
        </div>
      )}

      <footer className="site-footer">
        <div className="footer-main"><a className="brand brand--footer" href="#top"><img className="brand-mark" src={brandIcon} alt="" width={37} height={37} /><span className="brand-name"><strong>10K</strong> Books</span></a><p>Great ideas shouldn’t have to wait for Wi-Fi.</p></div>
        <div className="footer-links"><div><strong>Explore</strong><a href="#library">Library</a><a href="#experience">Experience</a><a href="#pricing">Pricing</a></div><div><strong>Legal</strong><a href="#/privacy">Privacy</a><a href="#/terms">Terms</a></div></div>
        <div className="footer-bottom"><span>© {new Date().getFullYear()} {appName}</span><span>Made for curious minds everywhere.</span></div>
      </footer>
    </div>
  )
}

export const WaitlistMode = (props: Omit<LandingPageProps, 'mode'>) => <LandingPage {...props} mode="waitlist" />
export const LaunchMode = (props: Omit<LandingPageProps, 'mode'>) => <LandingPage {...props} mode="launch" />
